import React from 'react';
import { Box, Header, SpaceBetween, Grid } from '@amzn/awsui-components-core';
import { EmptyStateMessage } from '../EmptyStateMessage';
import { LoadingSpinner } from 'src/components/generic-components/LoadingSpinner';
import { BusinessReportingCustomPromptSummary } from './BusinessReportingCustomPromptSummary';
import { BarLineChartWithProps } from '../fintech-ops-charts/BarChartWithProps';
import { i18nStringHour } from '../i18nStrings';
import { FinTechOpsCategories, FinTechOpsMessages, GRAPH_WIDTHS_BREAK_POINTS } from '../FinTechOpsConstants';
import { BusinessReportingFilters } from './BusinessReportingSelectionFilters';
import { ProcessedBusinessReportingUserData } from './BusinessReportingUserDataProcessing';
import { Titles } from 'src/components/das-finsuite/das-finsuite-constants';

interface BusinessReportingUserSectionProps {
  processedUserData: ProcessedBusinessReportingUserData;
  filters: BusinessReportingFilters;
  originalFilterOptions: string;
  isLoading: boolean;
}

export const BusinessReportingUserSection: React.FC<BusinessReportingUserSectionProps> = ({
  processedUserData,
  filters,
  originalFilterOptions,
  isLoading
}) => {
  const renderContent = () => {
    if (isLoading) {
      return <LoadingSpinner />;
    }

    // Check if we have processed data - if not, show empty state
    if (!processedUserData || processedUserData.aggregatedData.length === 0) {
      return <EmptyStateMessage title={FinTechOpsMessages.emptyStateMessage} subtitle={FinTechOpsMessages.emptyStateMessageDescription} />;
    }

    return (
      <SpaceBetween size="s">
        {/* AI Summary Box with Custom Prompt Functionality */}
        <BusinessReportingCustomPromptSummary
          title={`${Titles.TITLE_AI_SUMMARY}`}
          pageType={FinTechOpsCategories.businessUsers}
          statusKey="finished"
          statusValue="finished"
          filters={processedUserData.processedFilters}
          aggregatedData={JSON.stringify(processedUserData.aggregatedData)}
          processedUserData={processedUserData}
          businessFilters={filters}
          originalFilterOptions={originalFilterOptions}
          isLoading={isLoading}
        />

        {/* User Metrics Charts Grid */}
        <Grid
          gridDefinition={[{ colspan: GRAPH_WIDTHS_BREAK_POINTS }, { colspan: GRAPH_WIDTHS_BREAK_POINTS }, { colspan: GRAPH_WIDTHS_BREAK_POINTS }]}
        >
          <BarLineChartWithProps
            series={processedUserData.uniqueUsersByApplication}
            domainType="string"
            xDomain={processedUserData.uniqueUsersByApplicationXDomain}
            yDomain={processedUserData.uniqueUsersByApplicationYDomain}
            xScaleType="categorical"
            arialLabel="Total Unique Users by Application"
            errorText={FinTechOpsMessages.errorText}
            stackedBars={false}
            hideFilter={true}
            i18nStrings={i18nStringHour('Application Type')}
            emptyMessage={
              <EmptyStateMessage title={FinTechOpsMessages.emptyStateMessage} subtitle={FinTechOpsMessages.emptyStateMessageDescription} />
            }
            xTitle="Months"
            yTitle="Unique Users"
            noMatchMessage={FinTechOpsMessages.noMatchMessage}
            headerName="Total Unique Users by Application"
          />

          <BarLineChartWithProps
            series={processedUserData.top5AppsViewership}
            domainType="string"
            xDomain={processedUserData.top5AppsViewershipXDomain}
            yDomain={processedUserData.top5AppsViewershipYDomain}
            xScaleType="categorical"
            arialLabel="Top 5 Sub Application - Unique Users"
            errorText={FinTechOpsMessages.errorText}
            stackedBars={false}
            hideFilter={true}
            i18nStrings={i18nStringHour('Application')}
            emptyMessage={
              <EmptyStateMessage title={FinTechOpsMessages.emptyStateMessage} subtitle={FinTechOpsMessages.emptyStateMessageDescription} />
            }
            xTitle="Months"
            yTitle="Unique Users"
            noMatchMessage={FinTechOpsMessages.noMatchMessage}
            headerName="Top 5 Sub Application - Unique Users"
          />

          <BarLineChartWithProps
            series={processedUserData.viewershipByAppData}
            domainType="string"
            xDomain={processedUserData.viewershipByAppXDomain}
            yDomain={processedUserData.viewershipByAppYDomain}
            xScaleType="categorical"
            arialLabel="Total Views by Application"
            errorText={FinTechOpsMessages.errorText}
            stackedBars={processedUserData.viewershipByAppXDomain.length > 1}
            hideFilter={true}
            i18nStrings={i18nStringHour('Application')}
            emptyMessage={
              <EmptyStateMessage title={FinTechOpsMessages.emptyStateMessage} subtitle={FinTechOpsMessages.emptyStateMessageDescription} />
            }
            xTitle="Months"
            yTitle="Views"
            noMatchMessage={FinTechOpsMessages.noMatchMessage}
            headerName="Total Views by Application"
          />
        </Grid>
      </SpaceBetween>
    );
  };

  return (
    <div>
      <Header variant="h2">{Titles.TITLE_USER_METRICS}</Header>
      {renderContent()}
    </div>
  );
};
